import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { PRODUCT_MEDIA_BUCKET } from '@/types/database'

export interface UploadedImage {
  id: string
  url: string
  fileName: string
  status: 'uploading' | 'complete' | 'error'
  progress: number
  error?: string
}

interface UseImageUploadOptions {
  maxSizeBytes?: number
  allowedTypes?: string[]
  folder?: string
}

const DEFAULT_OPTIONS: UseImageUploadOptions = {
  maxSizeBytes: 10 * 1024 * 1024, // 10MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  folder: 'products',
}

const PRODUCT_MEDIA_BUCKET_FALLBACKS = ['product-images']
const PRODUCT_MEDIA_BUCKET_CANDIDATES = Array.from(
  new Set([PRODUCT_MEDIA_BUCKET, ...PRODUCT_MEDIA_BUCKET_FALLBACKS])
)

function isBucketNotFoundError(message: string | undefined): boolean {
  if (!message) return false
  return message.toLowerCase().includes('bucket not found')
}

export function useImageUpload(options: UseImageUploadOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const [uploadingImages, setUploadingImages] = useState<UploadedImage[]>([])

  const uploadImage = useCallback(async (file: File): Promise<UploadedImage | null> => {
    // Validate file type
    if (opts.allowedTypes && opts.allowedTypes.length > 0 && !opts.allowedTypes.includes(file.type)) {
      return {
        id: crypto.randomUUID(),
        url: '',
        fileName: file.name,
        status: 'error',
        progress: 0,
        error: `Invalid file type. Allowed: ${opts.allowedTypes?.join(', ')}`,
      }
    }

    // Validate file size
    if (file.size > (opts.maxSizeBytes || 0)) {
      const maxMB = (opts.maxSizeBytes || 0) / 1024 / 1024
      return {
        id: crypto.randomUUID(),
        url: '',
        fileName: file.name,
        status: 'error',
        progress: 0,
        error: `File too large. Maximum size: ${maxMB}MB`,
      }
    }

    const uploadId = crypto.randomUUID()
    const timestamp = Date.now()
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    const fileName = `${opts.folder}/${timestamp}-${uploadId}.${ext}`

    // Add to uploading state
    const uploadingImage: UploadedImage = {
      id: uploadId,
      url: '',
      fileName: file.name,
      status: 'uploading',
      progress: 0,
    }

    setUploadingImages(prev => [...prev, uploadingImage])

    try {
      // Upload to Supabase Storage. Retry legacy bucket names if needed.
      let uploadPath: string | null = null
      let uploadBucket: string | null = null
      let lastErrorMessage: string | null = null

      for (const bucket of PRODUCT_MEDIA_BUCKET_CANDIDATES) {
        const { data, error } = await supabase.storage
          .from(bucket)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          })

        if (!error && data?.path) {
          uploadPath = data.path
          uploadBucket = bucket
          break
        }

        lastErrorMessage = error?.message || 'Upload failed'
        if (!isBucketNotFoundError(lastErrorMessage)) {
          break
        }
      }

      if (!uploadPath || !uploadBucket) {
        if (lastErrorMessage && isBucketNotFoundError(lastErrorMessage)) {
          throw new Error(
            `Storage bucket not found. Checked ${PRODUCT_MEDIA_BUCKET_CANDIDATES.join(', ')}.`
          )
        }
        throw new Error(lastErrorMessage || 'Upload failed')
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(uploadBucket)
        .getPublicUrl(uploadPath)

      const completedImage: UploadedImage = {
        id: uploadId,
        url: publicUrl,
        fileName: file.name,
        status: 'complete',
        progress: 100,
      }

      setUploadingImages(prev =>
        prev.map(img => img.id === uploadId ? completedImage : img)
      )

      return completedImage
    } catch (error) {
      const errorImage: UploadedImage = {
        id: uploadId,
        url: '',
        fileName: file.name,
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed',
      }

      setUploadingImages(prev =>
        prev.map(img => img.id === uploadId ? errorImage : img)
      )

      return errorImage
    }
  }, [opts.allowedTypes, opts.maxSizeBytes, opts.folder])

  const uploadImages = useCallback(async (files: File[]): Promise<UploadedImage[]> => {
    const results = await Promise.all(files.map(uploadImage))
    return results.filter((r): r is UploadedImage => r !== null)
  }, [uploadImage])

  const removeUploadingImage = useCallback((id: string) => {
    setUploadingImages(prev => prev.filter(img => img.id !== id))
  }, [])

  const clearUploads = useCallback(() => {
    setUploadingImages([])
  }, [])

  return {
    uploadImage,
    uploadImages,
    uploadingImages,
    removeUploadingImage,
    clearUploads,
  }
}

/**
 * Extract YouTube video ID and generate thumbnail URL
 */
export function getYouTubeThumbnail(url: string): string | null {
  if (!url) return null

  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      // Return high-quality thumbnail
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
    }
  }

  return null
}
